import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './DrugPairEffectPopover.css';

/**
 * DrugPairEffectPopover — the click target of the Antidote / Toxicity pseudo
 * equip-card nodes in the equip cluster (SkillTreeOwnedCluster).
 *
 * These pseudo nodes surface the PLAYER-LEVEL persistent max-HP effect that the
 * drug-pair system (services/drug_pair_effects.py) applies when both drugs of a
 * recognized pair are equipped. The popover shows the TOTAL effect, stratified by
 * how it was determined among the equipped drugs: one row per contributing pair,
 * naming the two equipped drugs, the pair, its individual HP contribution, and
 * the clinical mechanism.
 *
 * `kind` is 'antidote' (green, +HP) or 'toxicity' (orange, -HP + masked-hypo
 * caution rows). Rendered as a centered modal via a body portal, matching the
 * read-only centered-modal behavior of the hotbar's other node clicks.
 */
export default function DrugPairEffectPopover({ kind, total, pairs = [], onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isAntidote = kind === 'antidote';
  const title = isAntidote ? 'Antidote' : 'Toxicity';
  const subtitle = isAntidote
    ? 'Reversal agents equipped with what they treat. Persistent bonus to your max HP.'
    : 'Opposed or interacting drugs equipped together. Persistent cost to your max HP.';
  const totalSign = total > 0 ? '+' : '';

  return createPortal(
    <div
      className={`dpe-scrim dpe-scrim--${kind}`}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} effects`}
      onClick={onClose}
    >
      <div className={`dpe-panel dpe-panel--${kind}`} onClick={(e) => e.stopPropagation()}>
        <header className="dpe-head">
          <div className="dpe-head-meta">
            <span className="dpe-eyebrow">Player Effect</span>
            <span className="dpe-title">{title}</span>
          </div>
          {Number(total) !== 0 && (
            <span className="dpe-total">{totalSign}{total} <span className="dpe-total-unit">max HP</span></span>
          )}
        </header>

        <p className="dpe-sub">{subtitle}</p>

        <div className="dpe-list">
          {pairs.map((p, i) => {
            const masked = p.type === 'masked_hypo';
            const delta = typeof p.hp_delta === 'number' ? p.hp_delta : 0;
            const deltaSign = delta > 0 ? '+' : '';
            return (
              <div className={`dpe-row${masked ? ' dpe-row--mask' : ''}`} key={`${p.name}-${i}`}>
                <div className="dpe-row-top">
                  <span className="dpe-drugs">{(p.drugs || []).join(' + ')}</span>
                  {masked ? (
                    <span className="dpe-delta dpe-delta--mask">masks HP</span>
                  ) : (
                    <span className="dpe-delta">{deltaSign}{delta}</span>
                  )}
                </div>
                <div className="dpe-name">{p.name}</div>
                {p.mechanism && <div className="dpe-mech">{p.mechanism}</div>}
              </div>
            );
          })}
        </div>

        <footer className="dpe-foot">
          <button type="button" className="dpe-close" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
